import React, { useState, useEffect, useContext, createContext } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useParams, useSearchParams, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { getDoc, doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { auth, db } from './firebase';
import Homepage from './components/Homepage';
import StadiumListing from './components/StadiumListing';
import StadiumDetail from './components/StadiumDetail';
import BookingPage from './components/BookingPage';
import LoginPage from './components/LoginPage';
import OwnerDashboard from './components/OwnerDashboard';
import SuperAdminPanel from './components/SuperAdminPanel';
import SeedDatabase from './components/SeedDatabase';
import MyBookings from './components/MyBookings';
import FavoritesPage from './components/FavoritesPage';
import ScrollToTop from './components/ScrollToTop';
import Footer from './components/Footer.js';
import Header from './components/Header';
import CustomerLoginModal from './components/CustomerLoginModal';
import ThemeProvider from './components/ThemeProvider';

import './App.css';

const AppContext = createContext(null);
export const useAppContext = () => {
  return useContext(AppContext);
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [language, setLanguage] = useState('en');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [bookingIntent, setBookingIntent] = useState(null);
  
  // --- NEW: Favorites State ---
  const [favorites, setFavorites] = useState([]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        const userDocRef = doc(db, 'users', currentUser.uid);
        getDoc(userDocRef).then((docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            setUser({ ...currentUser, ...data });
            setFavorites(data.favorites || []); // Load favorites
          } else {
            setUser(currentUser);
            setFavorites([]);
          }
          setLoading(false);
        }).catch((error) => {
          console.error("Error fetching user profile:", error);
          setUser(currentUser);
          setLoading(false);
        });
      } else {
        setUser(null);
        setFavorites([]);
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // --- NEW: Toggle Favorite Function ---
  const toggleFavorite = async (stadiumId) => {
    if (!user) {
      setShowLoginModal(true); // Require login
      return;
    }

    const isFavorite = favorites.includes(stadiumId);
    let newFavorites;

    // 1. Optimistic UI Update (Instant)
    if (isFavorite) {
      newFavorites = favorites.filter(id => id !== stadiumId);
    } else {
      newFavorites = [...favorites, stadiumId];
    }
    setFavorites(newFavorites);

    // 2. Update Firestore in background
    try {
      const userDocRef = doc(db, 'users', user.uid);
      if (isFavorite) {
        await updateDoc(userDocRef, {
          favorites: arrayRemove(stadiumId)
        });
      } else {
        await updateDoc(userDocRef, {
          favorites: arrayUnion(stadiumId)
        });
      }
    } catch (error) {
      console.error("Error updating favorites:", error);
      // Revert on error
      setFavorites(favorites);
    }
  };

  return (
    <ThemeProvider>
      <AppContext.Provider value={{ 
        user, loading, language, setLanguage, 
        showLoginModal, setShowLoginModal, 
        bookingIntent, setBookingIntent,
        favorites, toggleFavorite
      }}>
        <Router>
          <ScrollToTop />
          <AppRoutes />
        </Router>
      </AppContext.Provider>
    </ThemeProvider>
  );
}

function AppRoutes() {
  const { user, loading, showLoginModal, setShowLoginModal } = useAppContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const showSeed = searchParams.get('seed') === 'true';

  if (showSeed) { return <SeedDatabase />; }

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center transition-colors">
        <div className="text-xl text-neutral-600 dark:text-neutral-300">Loading...</div>
      </div>
    );
  }

  // Check for Super Admin first (must be before owner check)
  if (user && user.email === 'admin@bookstadium.com') {
    console.log('[App] Rendering SuperAdminPanel for:', user.email);
    return <SuperAdminPanel />;
  }

  // Check for Stadium Owner (email/password login)
  if (user && user.email && user.providerData[0]?.providerId === 'password') {
    console.log('[App] Rendering OwnerDashboard for:', user.email);
    return <OwnerDashboard />;
  }

  console.log('[App] Rendering normal customer routes for:', user?.email || 'guest');

  return (
    <div className="relative min-h-screen flex flex-col bg-neutral-50 dark:bg-neutral-900 transition-colors">
      <Header />
      <div className="flex-grow">
        <Routes>
          <Route path="/" element={<HomepageRoute user={user} />} />
          <Route path="/stadiums" element={<StadiumListingRoute user={user} />} />
          <Route path="/stadium/:stadiumId" element={<StadiumDetailRoute user={user} />} />
          <Route path="/stadium/:stadiumId/book/:sportId" element={<BookingRoute user={user} />} />
          <Route path="/login" element={<LoginPage onLoginSuccess={() => navigate('/')} />} />
          <Route path="/my-bookings" element={<MyBookingsRoute user={user} />} />
          {/* --- NEW ROUTE --- */}
          <Route path="/favorites" element={<FavoritesRoute user={user} />} />
        </Routes>
      </div>
      <Footer />
      {showLoginModal && (
        <CustomerLoginModal 
          onClose={() => setShowLoginModal(false)}
          onLoginSuccess={() => setShowLoginModal(false)}
        />
      )}
    </div>
  );
}

// (Other route components are unchanged...)
function HomepageRoute({ user }) {
  const navigate = useNavigate();
  return (
    <Homepage
      user={user}
      onSelectCategory={(category, query = '') => {
        if (query) {
          navigate(`/stadiums?category=${category}&search=${encodeURIComponent(query)}`);
        } else {
          navigate(`/stadiums?category=${category}`);
        }
      }}
      onSelectStadium={(id) => navigate(`/stadium/${id}`)}
    />
  );
}
function StadiumListingRoute({ user }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const category = searchParams.get('category') || 'all';
  const searchQuery = searchParams.get('search') || '';
  return (
    <StadiumListing
      user={user}
      category={category}
      searchQuery={searchQuery}
      onSelectStadium={(id) => navigate(`/stadium/${id}`)}
    />
  );
}
function StadiumDetailRoute({ user }) {
  const navigate = useNavigate();
  const { stadiumId } = useParams();
  return (
    <StadiumDetail
      user={user}
      stadiumId={stadiumId}
      onSelectSport={(stadiumId, sport) => {
        navigate(`/stadium/${stadiumId}/book/${sport.id}`);
      }}
    />
  );
}
function BookingRoute({ user }) {
  const { stadiumId, sportId } = useParams();
  return (
    <BookingPage 
      user={user}
      stadiumId={stadiumId}
      sportId={sportId}
    />
  );
}
function MyBookingsRoute({ user }) {
  if (!user) { return <Navigate to="/" replace />; }
  return <MyBookings />;
}

// --- NEW ROUTE COMPONENT ---
function FavoritesRoute({ user }) {
  if (!user) { return <Navigate to="/" replace />; }
  return <FavoritesPage />;
}

export default App;